import { createRef, useMemo, useState } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import { BezonedMenuTabs } from "../../components/ui/common/bezoned-menu-tabs.js";
import type { BezonedMenuItem } from "../../components/ui/common/bezoned-menu-tabs.js";
import { BaseModal } from "../../components/ui/modals/base-modal.jsx";
import type { SharedModalProps } from "../../components/ui/modals/base-modal.jsx";
import { SettingsUserAudioDevices } from "./tabs/settings-user-audio-devices.js";
import { SettingsUserLayout } from "./tabs/settings-user-layout.js";
import { SettingsUserNotifications } from "./tabs/settings-user-notifications.js";
import { SettingsUserProfile } from "./tabs/settings-user-profile.js";
import { SettingsUserPushToTalk } from "./tabs/settings-user-push-to-talk.js";

const menuItems: BezonedMenuItem[] = [
  {
    component: <SettingsUserAudioDevices />,
    label: "Devices",
  },
  {
    component: <SettingsUserPushToTalk />,
    label: "Push To Talk",
  },
  {
    component: <SettingsUserNotifications />,
    label: "Notifications",
  },
  {
    component: <SettingsUserLayout />,
    label: "Layout",
  },
  {
    component: <SettingsUserProfile />,
    label: "Profile",
  },
  // Hidden as per: https://bezoned.atlassian.net/browse/BZVO-734
  // {
  //   label: "Noise Suppression",
  //   component: <NoiseSuppressionSettings />,
  // },
] as const;

interface ContentProps {
  items: BezonedMenuItem[];
  currentTab: string;
}

const Content = ({ items, currentTab }: ContentProps) => {
  const transitionRefs = useMemo(
    () => new Map([[currentTab, createRef<HTMLDivElement>()]]),
    [currentTab]
  );
  const transitionRef = transitionRefs.get(currentTab);
  if (!transitionRef) {
    throw new Error(`Missing transition ref for tab: ${currentTab}`);
  }

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition
        classNames="fade"
        key={currentTab}
        nodeRef={transitionRef}
        timeout={200}
      >
        <div className="w-full" ref={transitionRef}>
          {items.find((item) => item.label === currentTab)?.component ?? (
            <div>This tab is not available</div>
          )}
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
};

export const SettingsUser = (_props: SharedModalProps) => {
  // Lint exception: We can assert that this won't be null
  const [currentTab, setCurrentTab] = useState(menuItems[0]!.label);

  return (
    <BaseModal title="Settings">
      <div className="flex h-full flex-col lg:flex-row">
        <div className="border-bzd-border-100 w-full border-b p-4 lg:h-full lg:w-[200px] lg:flex-shrink-0 lg:border-r lg:border-b-0">
          <BezonedMenuTabs
            currentTab={currentTab}
            items={menuItems}
            selectTab={setCurrentTab}
          />
        </div>
        <div className="flex overflow-y-auto p-4 lg:w-[calc(100%-200px)]">
          <Content currentTab={currentTab} items={menuItems} />
        </div>
      </div>
    </BaseModal>
  );
};
